import {
  CompanyPageCompanyFragment,
  useAtsAllRejectionReasonsQuery,
  useAtsAllStagesQuery,
  useAtsSettingsQuery,
} from 'generated/graphql';
import { AssessmentPackagesCard } from './AssessmentPackagesCard';
import { CoderbyteCard } from './CoderbyteCard';
import { ConnectionCard } from './ConnectionCard';
import styles from './IntegrationsTab.module.scss';
import { RejectionCard } from './RejectionCard';
import { ResultsDeliveryCard } from './ResultsDeliveryCard';
import { StageMappingCard } from './StageMappingCard';
import { StageRejectionReasonsCard } from './StageRejectionReasonsCard';

type IntegrationsTabProps = {
  company: CompanyPageCompanyFragment;
};

export function IntegrationsTab({ company }: IntegrationsTabProps) {
  const { data, loading } = useAtsSettingsQuery({
    variables: { companyId: company.id },
    fetchPolicy: 'cache-and-network',
  });
  const atsSettings = data?.atsSettings ?? null;

  const { data: reasonsData, loading: reasonsLoading } =
    useAtsAllRejectionReasonsQuery({
      variables: { companyId: company.id },
      skip: !atsSettings,
    });
  const reasonOptions = [...(reasonsData?.atsAllRejectionReasons ?? [])]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(reason => ({ label: reason.name, value: reason.id }));

  const { data: stagesData, loading: stagesLoading } = useAtsAllStagesQuery({
    variables: { companyId: company.id },
    skip: !atsSettings,
  });
  // Pipeline order is already applied server-side; keep it.
  const stageOptions = (stagesData?.atsAllStages.stages ?? []).map(stage => ({
    label: stage.name,
    value: stage.atsStageRef,
  }));

  return (
    <div className={styles.container}>
      <ConnectionCard company={company} atsSettings={atsSettings} />

      {/* Independent of the ATS integration — a company can have Coderbyte
          coding assessments without an ATS connected, and vice versa. */}
      <CoderbyteCard companyId={company.id} />

      {/* The ATS-backed cards only make sense once a Kombo integration
          exists. Kept silent while loading to avoid a flash of the
          "not connected" copy on first paint. */}
      {!loading && !atsSettings && (
        <p className={styles.emptyState}>
          No ATS integration is connected for this company. Stage mapping,
          rejection and results-delivery settings appear once one is.
        </p>
      )}

      {atsSettings && (
        <>
          <StageMappingCard
            companyId={company.id}
            atsIdentifier={company.atsIdentifier}
            atsSettings={atsSettings}
            stageOptions={stageOptions}
            stagesLoading={stagesLoading}
            missingRefCount={stagesData?.atsAllStages.missingRefCount ?? 0}
          />
          <StageRejectionReasonsCard
            companyId={company.id}
            atsIdentifier={company.atsIdentifier}
            atsSettings={atsSettings}
            stageOptions={stageOptions}
            stagesLoading={stagesLoading}
            reasonOptions={reasonOptions}
            reasonsLoading={reasonsLoading}
          />
          <RejectionCard
            companyId={company.id}
            atsIdentifier={company.atsIdentifier}
            atsSettings={atsSettings}
            reasonOptions={reasonOptions}
            reasonsLoading={reasonsLoading}
          />
          <ResultsDeliveryCard
            companyId={company.id}
            atsIdentifier={company.atsIdentifier}
            atsSettings={atsSettings}
            stageOptions={stageOptions}
            stagesLoading={stagesLoading}
          />
          <AssessmentPackagesCard companyId={company.id} />
        </>
      )}
    </div>
  );
}
